import type { GraphQLFormattedError } from 'graphql'

/** How many distinct messages one operation's log line carries. */
const MAX_DISTINCT_MESSAGES = 5

export interface GraphQLErrorSummary {
  /** Every error the response carried. */
  total: number
  /** The most frequent distinct messages, most frequent first, with one path each. */
  messages: { message: string; count: number; path?: (string | number)[] }[]
}

/**
 * Folds a response's `errors` into one summary.
 *
 * A single response can carry one error per row: a page of 500 points whose
 * `series` field times out upstream answers 500 identical errors, one per edge.
 * Logged one by one, that single request was 500 error lines — enough on its own
 * to cross the paging monitor's 10-errors-an-hour threshold.
 */
export function summarizeGraphQLErrors(
  errors: ReadonlyArray<GraphQLFormattedError>
): GraphQLErrorSummary {
  const byMessage = new Map<
    string,
    { count: number; path?: (string | number)[] }
  >()

  for (const { message, path } of errors) {
    const entry = byMessage.get(message)
    if (entry) {
      entry.count += 1
    } else {
      byMessage.set(message, {
        count: 1,
        path: path ? [...path] : undefined,
      })
    }
  }

  const messages = [...byMessage.entries()]
    .map(([message, { count, path }]) => ({ message, count, path }))
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_DISTINCT_MESSAGES)

  return { total: errors.length, messages }
}

/**
 * Whether the response still answered something: `data` holds at least one
 * non-null root field. Errors beside data are field-level failures (a resolver
 * upstream timing out for some rows) — the screen renders what came back, so
 * they are a warning, not a Console error.
 */
export function hasPartialData(
  data: Record<string, unknown> | null | undefined
): boolean {
  return !!data && Object.values(data).some((value) => value != null)
}

/**
 * Network failures that are the caller's state, not a Console fault: an
 * expired or revoked token (401) or one without the grant (403).
 */
export function isAuthStatus(statusCode: number | undefined): boolean {
  return statusCode === 401 || statusCode === 403
}
